import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { range } from "lodash";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { Add, Check, Clear, Edit, ExpandLess, ExpandMore } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { saveActivationRecipe, sizeMeta } from "@/api";
import { useActivationContext, useAudienceContext } from "@/context";
import { FilterBox, NumberBox } from "@/components/atoms";
import {
  Activation,
  MetaDemogs,
  MetaInterest,
  MetaSize,
  MetaSizeRequest,
} from "@/types";
import {
  abbreviateNumber,
  countryOptions,
  genderOptions,
  getOption,
  prepareGender
} from "@/utils";

interface MetaRecipePanelProps {
  activation: Activation;
  onError: (error: string | null) => void;
  setEditMode: (value: boolean) => void;
  editMode: boolean;
  open: boolean;
  toggleOpen: () => void;
}

const MetaRecipePanel: React.FC<MetaRecipePanelProps> = ({
  activation,
  onError,
  setEditMode,
  editMode,
  open,
  toggleOpen,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { dispatch } = useAudienceContext();
  const theme = useTheme();
  const {
    selection,
    activeSelection,
    setActiveSelection,
    addSelection,
    removeSelection,
    removeInterest
  } = useActivationContext();

  const [saving, setSaving] = useState<boolean>(false);
  const [size, setSize] = useState<MetaSize | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSaveRecipe = async () => {
    const token = await getAccessTokenSilently();
    if (!activation || !activation.meta_demogs) return;
    setSaving(true);
    onError(null);

    const recipe = {
      meta_demogs: activation?.meta_demogs,
      meta_interests: selection,
    };
    const { data, error } = await saveActivationRecipe(
      token,
      activation.campaign_id,
      recipe,
    );

    if (data) {
      setEditMode(false);
      setSaving(false);
      setLoading(true);

      const { data: sizeData, error: sizeError } = await sizeMeta(
        token,
        prepareTargetSpec(selection),
      );

      if (sizeData) {
        setSize(sizeData);
        setLoading(false);
      }
      if (sizeError) {
        onError(sizeError.message);
        setLoading(false);
      }
    }

    if (error) {
      onError(error.message);
      setSaving(false);
    }
  };

  const prepareTargetSpec = (selection: MetaInterest[][]): MetaSizeRequest => {
    return {
      targeting_spec: {
        geo_locations: { countries: [activation?.meta_demogs?.country ?? 'GB'] },
        age_min: activation?.meta_demogs?.age_min ?? 0,
        age_max: activation?.meta_demogs?.age_max ?? 65,
        flexible_spec: selection.map(x => ({interests: x.map(y => ({id: y.meta_id, name: y.meta_name}) )})),
        genders: prepareGender(activation?.meta_demogs?.gender ?? 'both')
      },
    };
  };

  const handleAgeChange =
    (name: keyof MetaDemogs) => (event: SelectChangeEvent<number>) => {
      if (activation && activation.meta_demogs) {
        dispatch({
          type: "UPDATE_ACTIVATION",
          payload: {
            ...activation,
            meta_demogs: {
              ...activation.meta_demogs,
              [name]: event.target.value,
            },
          },
        });
      }
    };

  const handleDemogChange =
    (name: keyof MetaDemogs) => (event: SelectChangeEvent) => {
      if (activation && activation.meta_demogs) {
        dispatch({
          type: "UPDATE_ACTIVATION",
          payload: {
            ...activation,
            meta_demogs: {
              ...activation.meta_demogs,
              [name]: event.target.value,
            },
          },
        });
      }
    };

  const handleEdit = () => {
    setSize(null);
    setEditMode(true);
  };

  const handleSelect = (id: number) => {
    setActiveSelection(id);
    setEditMode(true);
  }

  return (
    <Box
      position="sticky"
      bottom={0}
      zIndex={1}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderTop: "2px solid",
        borderColor: theme.palette.primary.main,
        p: open ? 3 : 0,
        mx: -3,
      }}
    >
      <Collapse in={open} collapsedSize={58} timeout={200}>

        {/* Header */}
        <Box
          height={58}
          onClick={toggleOpen}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            p: open ? 0 : 3
        }}
        >
          <Typography variant="h6" color="primary">
            Audience Recipe
          </Typography>
          {open ? <ExpandLess /> : <ExpandMore />}
        </Box>

        {/* Content */}
        <Stack spacing={1}>
          <Typography variant="body2" color="textSecondary">
            Refine your selection here and click Save to estimate an audience size
            and convert these interests into a list that you can copy and paste into
            AdsManager
          </Typography>
          {activation.meta_demogs && (
            <Stack direction="row" spacing={3} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Age Range:</Typography>
                {editMode ? (
                  <>
                    <NumberBox
                      label="Min Age"
                      value={activation.meta_demogs.age_min}
                      options={range(18, activation.meta_demogs.age_max)}
                      onChange={handleAgeChange("age_min")}
                    />
                    <NumberBox
                      label="Max Age"
                      value={activation.meta_demogs.age_max}
                      options={range(activation.meta_demogs.age_min + 1, 66)}
                      onChange={handleAgeChange("age_max")}
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="secondary">
                    {`${activation.meta_demogs.age_min}-${activation.meta_demogs.age_max}`}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Gender:</Typography>
                {editMode ? (
                  <>
                    <FilterBox
                      label={''}
                      value={activation.meta_demogs.gender}
                      options={genderOptions}
                      onChange={handleDemogChange("gender")}
                      showSelected
                      width={100}
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="secondary">
                    {`${getOption(activation.meta_demogs.gender, genderOptions)}`}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Country:</Typography>
                {editMode ? (
                  <>
                    <FilterBox
                      label={''}
                      value={activation.meta_demogs.country}
                      options={countryOptions}
                      onChange={handleDemogChange("country")}
                      showSelected
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="secondary">
                    {`${getOption(activation.meta_demogs.country, countryOptions)}`}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
          <Box>
            <Typography variant="body2">Interests</Typography>
          </Box>
          {selection.map((group, idx) => (
            <React.Fragment key={idx}>
              {idx === 0 ?
                <Typography variant="caption" color="textSecondary">
                  People who match
                </Typography>
                :
                <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                  <Typography variant="caption" color="textSecondary">
                    and must also match
                  </Typography>
                  <IconButton
                    onClick={() => removeSelection(idx)}
                    sx={{ p: 0}}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </Box>
              }
              <Box
                sx={{
                  minHeight: "48px",
                  maxHeight: "114px",
                  overflowY: "auto",
                  border: (editMode && idx === activeSelection) ? 2: 1,
                  borderRadius: 1,
                  borderColor: (editMode && idx === activeSelection)
                    ? theme.palette.secondary.main
                    : theme.palette.divider,
                  p: 1,
                  cursor: "pointer"
                }}
                onClick={() => handleSelect(idx)}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                  }}
                >
                  {editMode ? (
                    group.map((meta_interest) => (
                      <Chip
                        key={meta_interest.meta_id}
                        size="small"
                        color={idx === activeSelection ? "secondary" : "default"}
                        label={meta_interest.meta_name}
                        onDelete={() => removeInterest(meta_interest, idx)}
                        sx={{ m: 0.5 }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="secondary">
                      {group.map((item) => item.meta_name).join(", ")}
                    </Typography>
                  )}
                </Box>
              </Box>
            </React.Fragment>
            ))}
          {editMode &&
            <Box>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                disableElevation
                startIcon={<Add />}
                onClick={addSelection}
                fullWidth={false}
              >
                Narrow Audience
              </Button>
            </Box>
          }
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {loading ? (
                <CircularProgress size={32} />
              ) : (
                size &&
                !editMode && (
                  <Typography>
                    {`Audience Size: ${abbreviateNumber(size.lower_bound)} - ${abbreviateNumber(size.upper_bound)}`}
                  </Typography>
                )
              )}
            </Box>

            {editMode ? (
              <Button
                variant="contained"
                size="small"
                disableElevation
                startIcon={<Check />}
                loading={saving}
                onClick={handleSaveRecipe}
              >
                Save
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                disableElevation
                startIcon={<Edit />}
                onClick={handleEdit}
              >
                Edit
              </Button>
            )}
          </Box>
        </Stack>
      </Collapse>
    </Box>
  );
};

export default MetaRecipePanel;
